import { Injectable, NgZone, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BehaviorSubject, Observable, fromEvent } from 'rxjs';
import {
  map,
  filter,
  distinctUntilChanged,
  throttleTime,
  share
} from 'rxjs/operators';
export type ScrollDirection = 'up' | 'down';

/**
 * Service to track global scroll direction changes.
 * Optimized with throttling and direction change detection.
 * Sets scroll-direction attribute on app-root for CSS-driven styling.
 */
@Injectable({
  providedIn: 'root'
})
export class ScrollDirectionService {
  private readonly directionSubject = new BehaviorSubject<ScrollDirection>(
    'up'
  );

  /**
   * Observable stream of scroll direction changes.
   * Only emits when direction actually changes (up <-> down).
   */
  public readonly direction$: Observable<ScrollDirection> =
    this.directionSubject.asObservable();

  private prevScrollPos = 0;
  private readonly THROTTLE_MS = 16; // ~60fps
  private readonly SUPPRESS_DURATION_MS = 900;
  private suppressUntil = 0;
  private appRootElement: HTMLElement | null = null;
  private currentDirection: ScrollDirection | null = null;

  constructor(
    private readonly ngZone: NgZone,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    this.initScrollListener();
    this.initTabChangeListeners();
  }

  /**
   * Suppress scroll-direction updates for the next N ms.
   * Used when programmatic scrolls occur (e.g. Formio tab switch, wizard navigation).
   */
  public suppressProgrammaticScroll(
    durationMs = this.SUPPRESS_DURATION_MS
  ): void {
    this.suppressUntil = Date.now() + durationMs;
  }

  /**
   * Listen for window events that indicate tab-pane changes or programmatic navigation.
   * Suppresses scroll-direction updates to avoid misinterpreting programmatic scrolls.
   * Uses capture: true so we run before other listeners and suppress before any scroll.
   */
  private initTabChangeListeners(): void {
    const opts: AddEventListenerOptions = { capture: true };

    const onGoToNextPage = (): void => this.suppressProgrammaticScroll();
    const onGoToPreviousPage = (): void => this.suppressProgrammaticScroll();
    const onSaveDraft = (ev: Event): void => {
      const detail = (ev as CustomEvent)?.detail;
      if (detail?.id === 'tabsReview') {
        this.suppressProgrammaticScroll();
      }
    };

    // Tab pill click: Formio Tabs component calls setTab + scrollIntoView directly
    // in its click handler (no customEvent). Suppress in capture phase before it runs.
    const onTabPillClick = (ev: Event): void => {
      const target = ev.target as HTMLElement;
      if (target?.closest?.('.formio-component-tabs .nav-link')) {
        this.suppressProgrammaticScroll();
      }
    };

    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('goToNextPage', onGoToNextPage, opts);
      window.addEventListener('goToPreviousPage', onGoToPreviousPage, opts);
      window.addEventListener('saveDraft', onSaveDraft, opts);
      this.document.addEventListener('click', onTabPillClick, opts);
    });
  }

  /**
   * Initialize scroll event listener with performance optimizations.
   * Runs outside Angular zone for better performance.
   * Sets scroll-direction attribute on app-root element.
   */
  private initScrollListener(): void {
    this.appRootElement = this.document.querySelector('app-root');

    // Set initial direction attribute
    if (this.appRootElement) {
      this.appRootElement.setAttribute('scroll-direction', 'up');
      this.currentDirection = 'up';
    }

    // Run outside Angular zone to avoid unnecessary change detection
    this.ngZone.runOutsideAngular(() => {
      fromEvent(window, 'scroll', { passive: true })
        .pipe(
          // Throttle to ~60fps for performance
          throttleTime(this.THROTTLE_MS, undefined, {
            leading: true,
            trailing: true
          }),
          // Map scroll position to direction
          map((): ScrollDirection | null => {
            const currentScrollPos =
              window.scrollY || document.documentElement.scrollTop;
            const direction: ScrollDirection =
              currentScrollPos > this.prevScrollPos ? 'down' : 'up';
            this.prevScrollPos = currentScrollPos;
            const suppressed = Date.now() < this.suppressUntil;

            // Suppress updates during programmatic scrolls (Formio tab switch, etc.)
            if (suppressed) {
              return null;
            }
            return direction;
          }),
          filter((d): d is ScrollDirection => d !== null),
          // Only emit when direction changes
          distinctUntilChanged(),
          // Share subscription across multiple subscribers
          share()
        )
        .subscribe((direction) => {
          this.updateScrollDirectionAttribute(direction);

          // Run inside Angular zone to trigger change detection for subscribers
          this.ngZone.run(() => {
            this.directionSubject.next(direction);
          });
        });
    });
  }

  /**
   * Update scroll-direction attribute on app-root element.
   * Only updates if direction has changed to avoid unnecessary DOM mutations.
   */
  private updateScrollDirectionAttribute(direction: ScrollDirection): void {
    if (!this.appRootElement || this.currentDirection === direction) {
      return;
    }

    this.currentDirection = direction;
    this.appRootElement.setAttribute('scroll-direction', direction);
  }

  /**
   * Get current scroll direction synchronously.
   * Useful for initial state setup.
   */
  public getCurrentDirection(): ScrollDirection {
    return this.directionSubject.value;
  }
}
